/**
 * 0.38.65 - Shared "scroll the citizen's page to somewhere useful"
 * helper. Powers the new `action: 'scroll'` button action added in
 * the same ship. Two callers (PageContentRenderer public button
 * onClick, AdminFormPreview button onClick) share this so the
 * preview matches the public form.
 *
 * Usage:
 *   scrollToTarget('element', 'total_amount')
 *   scrollToTarget('top')
 *   scrollToTarget('first_error')
 *
 * `kind` values (matches the button's action_target field):
 *   - 'element'      Scroll to a specific field. Requires elementId.
 *                    Uses [data-element-id="..."] selector so both
 *                    inputs and their labels/wrappers can carry it.
 *   - 'top'          Scroll to the top of the form. Walks up from
 *                    the button element looking for <form>; falls
 *                    back to page top if none found.
 *   - 'first_error'  Scroll to the first field currently showing a
 *                    validation error. Uses .field-error-bar (the
 *                    platform's Henaket error class) in document
 *                    order. No error → no-op silently.
 *
 * Behavior:
 *   - Smooth scroll animation by default.
 *   - Respects prefers-reduced-motion: 'auto' behavior instead.
 *   - Uses scrollIntoView({block:'start'}) when the target is
 *     taller than the viewport so its top pins to the top of the
 *     screen; uses {block:'nearest'} when it fits so it doesn't
 *     scroll if already in view.
 *   - Sticky header offset is handled via CSS scroll-margin-top
 *     on the target's wrapper (set globally in styles/global.css
 *     alongside this ship). The scroll math needs no manual
 *     header height math.
 *
 * Missing target (no matching element for `element`, no <form>
 * for `top`, no errors for `first_error`): silent no-op. Debug
 * logging when ?scrolldebug=1 is on.
 */

const _log = (...args) => {
  try {
    if (typeof window === 'undefined') return;
    const q = new URLSearchParams(window.location.search || '');
    if (q.get('scrolldebug') === '1' || window.__DEBUG_SCROLL === true) {
      // eslint-disable-next-line no-console
      console.log('[scrollToTarget]', ...args);
    }
  } catch (_e) { /* silent */ }
};

function _prefersReducedMotion() {
  try {
    return typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_e) {
    return false;
  }
}

/**
 * Perform a scroll to the given node using best-practice options.
 * Chooses block:'start' when the target is taller than the viewport
 * (so its top pins), block:'nearest' otherwise (no unnecessary
 * jumps when already visible).
 */
function _scrollNodeIntoView(node) {
  if (!node || typeof node.scrollIntoView !== 'function') return;

  // 0.38.85 - Deterministic reveal, replacing the direction-dependent
  // scrollIntoView({block:'nearest'}) heuristic.
  //
  // The old behavior: block 'start' when taller than the viewport,
  // else 'nearest'. 'nearest' does the MINIMUM scroll, so a target
  // ABOVE the fold top-aligned nicely but a target BELOW (e.g. under
  // the Calculate button) only bottom-aligned or showed its top -
  // inconsistent by direction.
  //
  // New behavior, computed explicitly so it's the same regardless of
  // scroll direction:
  //   - Header offset: read --header-height (fallback 88) so the
  //     target clears the sticky header. 24px comfort gap on top.
  //   - If the WHOLE element + a 24px bottom gap fits in the space
  //     below the header -> scroll so the element sits fully in view
  //     with the 24px breathing room (element top just below header).
  //   - If the element is taller than that space -> pin its top just
  //     below the header (can't show it all; top is the useful part).
  // Either way the element's TOP lands at the same place, so short
  // and tall targets behave predictably and up/down are identical.
  const GAP = 24;
  let headerH = 88;
  try {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height');
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed) && parsed >= 0) headerH = parsed;
  } catch (_e) { /* keep default */ }

  const behavior = _prefersReducedMotion() ? 'auto' : 'smooth';

  try {
    const rect = node.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    // Absolute document Y of the element's top.
    const elemTopAbs = rect.top + scrollY;
    // Space available between the sticky header and the viewport
    // bottom (used only for the debug log / fit check).
    const usable = vh - headerH;
    const fitsWithGap = rect.height + GAP <= usable;

    // Top-align in both cases: element top just below the header with
    // a 24px gap. When it fits, the whole element (+ its own bottom
    // margin) is visible with room to spare; when it's taller, the
    // top is shown and the rest extends downward. Same formula either
    // way, so up and down scrolls are identical and predictable.
    let targetScroll = elemTopAbs - headerH - GAP;
    if (targetScroll < 0) targetScroll = 0;

    window.scrollTo({ top: targetScroll, behavior });
    _log('scrolled', { elemTopAbs, headerH, usable, height: rect.height, fitsWithGap, targetScroll });
    return;
  } catch (_e) {
    // Fallback to the old API if measurement fails for any reason.
    try {
      node.scrollIntoView({ behavior, block: 'start' });
    } catch (_e2) {
      try { node.scrollIntoView(); } catch (_e3) { /* give up quietly */ }
    }
  }
}

function _findElementById(elementId) {
  if (!elementId || typeof document === 'undefined') return null;
  // Primary: data attribute set by every scrollable form-element
  // wrapper. Falls back to id="f-<elementId>" and the input's
  // name="<elementId>" for robustness across renderers.
  const q1 = document.querySelector('[data-element-id="' + CSS.escape(elementId) + '"]');
  if (q1) return q1;
  const q2 = document.getElementById('f-' + elementId);
  if (q2) return q2;
  const q3 = document.querySelector('[name="' + CSS.escape(elementId) + '"]');
  if (q3) {
    // Return the enclosing wrapper if available so labels + inputs
    // scroll together.
    return q3.closest('[data-element-id]') || q3;
  }
  return null;
}

function _findFormRoot(startNode) {
  if (!startNode || typeof document === 'undefined') return null;
  if (typeof startNode.closest === 'function') {
    const f = startNode.closest('form');
    if (f) return f;
  }
  // Fallback: first form in the document.
  return document.querySelector('form') || null;
}

function _findFirstError() {
  if (typeof document === 'undefined') return null;
  // Platform's Henaket error indicator - a wrapper class applied to
  // the field when it has an active validation error.
  const first = document.querySelector('.field-error-bar');
  if (first) {
    // If the error-bar wrapper itself isn't an element wrapper,
    // climb to the nearest data-element-id ancestor for a cleaner
    // scroll target (so the label is included in the scrolled area).
    return first.closest('[data-element-id]') || first;
  }
  return null;
}

/**
 * Public entry. `sourceNode` is optional - it's the element that
 * triggered the scroll (typically the button itself). Used to
 * locate the enclosing <form> for kind='top'.
 */
export function scrollToTarget(kind, elementId, sourceNode) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  _log('kind=', kind, 'elementId=', elementId);
  let node = null;
  if (kind === 'element') {
    node = _findElementById(elementId);
    if (!node) {
      _log('no element found for id=', elementId);
      return;
    }
  } else if (kind === 'top') {
    node = _findFormRoot(sourceNode);
    if (!node) {
      _log('no form root found; falling back to window scroll top');
      const behavior = _prefersReducedMotion() ? 'auto' : 'smooth';
      try { window.scrollTo({ top: 0, behavior }); }
      catch (_e) { window.scrollTo(0, 0); }
      return;
    }
  } else if (kind === 'first_error') {
    node = _findFirstError();
    if (!node) {
      _log('no field with an active error');
      return;
    }
  } else {
    _log('unknown kind:', kind);
    return;
  }
  _scrollNodeIntoView(node);
}
